import React, {useCallback, useEffect, useState} from 'react';
import {Button, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip} from '@mui/material';
import {
    useAssignRolePermissionsMutation,
    useGetPermissionsQuery,
    useGetRolesQuery
} from '../../../../api/services/accountService';
import {ROLES, ROLES_LABELS} from '../../../../constants/roles';
import PropTypes from 'prop-types';
import AddTaskIcon from '@mui/icons-material/AddTask';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import StudentProfileAccessRestrictions from './StudentProfileAccessRestrictions';

/* eslint-disable react/prop-types */

const COLUMNS_ORDER = {
    [ROLES.SCHOOL_ADMIN]: 1,
    [ROLES.MEMBER_OF_STAFF]: 2,
    [ROLES.SENIOR_MEMBER_OF_STAFF]: 3,
    [ROLES.EMPLOYEE]: 4,
    [ROLES.PARENT]: 5
}

// Maria, 19 October 2026: shorter names on screen only; the sections keep their stored names
const SHORT_NAMES = {
    'Student Dietary Needs And Food Allergies': 'Student Dietary Needs',
    'Applicant Dietary Needs And Food Allergies': 'Applicant Dietary Needs',
    'Student Pupil Premium and Young Carers': 'Student Pupil Premium',
    'Applicant Pupil Premium and Young Carers': 'Applicant Pupil Premium',
    'Student Special Educational Needs': 'Student SEN',
    'Applicant Special Educational Needs': 'Applicant SEN',
    'Student Medical OTC Consents': 'Student OTC Medicine',
    'Applicant Medical OTC Consents': 'Applicant OTC Medicine',
    'Staff Identification & Banking': 'Staff Banking'
};

// Maria, 19 October 2026: Next of Kin and its address are one permission. The address row is not listed, and it
// takes the same View, Edit or Hide as Next of Kin whenever that is changed.
// Maria, 19 October 2026: Manage Access in three parts, Student, Applicant (with Enquiry) and Staff
const PARTS = ['Student', 'Applicant', 'Staff'];
// Maria, 19 October 2026: a fourth button for the student profile access restrictions, in place of a section below
const RESTRICTED = 'Restrict Access';
const partOf = key => (key.startsWith('Staff')
    ? 'Staff'
    : key.startsWith('Applicant') || key.startsWith('Enquiry') ? 'Applicant' : 'Student');

const HIDDEN_SECTIONS = ['ApplicantAdmissionCategories', 'StaffNextOfKinAddress'];
const FOLLOWS = {StaffNextOfKin: 'StaffNextOfKinAddress'};

const PermissionSelect = ({permissions, value, onChange}) =>
    (
        <ToggleButtonGroup
            color="primary"
            value={value}
            exclusive
            onChange={(e, permission) => onChange(permission)}
        >
            {
                permissions.map(permission => (
                    <ToggleButton
                        sx={{
                            textTransform: 'none',
                            '&.Mui-selected': {
                                backgroundColor: '#186BBE',
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: '#155FA7'
                                }
                            }
                        }}
                        key={permission.value}
                        value={permission.value}
                        disabled={permission.disabled}
                        size="small"
                        // Maria, 19 October 2026: smaller buttons, so more rows fit on the screen
                        style={{padding: '1px 5px', fontSize: 12, lineHeight: 1.6, minWidth: 0}}
                    >
                        {permission.name}
                    </ToggleButton>
                ))
            }
        </ToggleButtonGroup>
    );

PermissionSelect.propTypes = {
    permissions: PropTypes.array.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
};

const AccessSettings = () => {
    const {data: permissionModules = [], isLoading: isPermissionsLoading} = useGetPermissionsQuery();
    const {data: roles = [], isLoading: isRolesLoading} = useGetRolesQuery();
    const [assignPermissions, {isLoading: isAssignPermissionsInProgress}] = useAssignRolePermissionsMutation();
    const [permissions, setPermissions] = useState({});
    const [originalPermissionsSnapshot, setOriginalPermissionsSnapshot] = useState({});
    // the section names wrap only when the table would otherwise scroll sideways; checked again whenever the table's
    // width changes, starting from one line
    const [namesWrap, setNamesWrap] = useState(false);
    const [part, setPart] = useState('Student');
    const [addRequest, setAddRequest] = useState(0);
    const visibleModules = permissionModules
        .filter(module => !HIDDEN_SECTIONS.includes(module.key) && partOf(module.key) === part);
    // a role is shown only when at least one of its settings can be changed in this part
    const roleCanBeSet = role => visibleModules
        .some(module => module.permissions.some(permission => !permission.disabledForRoleIds.includes(role.id)));
    const containerRef = useCallback(container => {
        if (!container || typeof ResizeObserver === 'undefined') return;
        let lastWidth = 0;
        new ResizeObserver(() => {
            if (container.clientWidth === lastWidth) return;
            lastWidth = container.clientWidth;
            setNamesWrap(false);
            requestAnimationFrame(() => setNamesWrap(container.scrollWidth > container.clientWidth + 1));
        }).observe(container);
    }, []);
    const isLoading = isPermissionsLoading ||
        isRolesLoading ||
        isAssignPermissionsInProgress;

    const handlePermissionChange = useCallback((roleId, module, value) => {
        if (value) {
            // the same View, Edit or Hide for a section that follows this one
            const follower = FOLLOWS[module];
            const level = permissionModules.find(m => m.key === module)?.permissions.find(p => p.value === value)?.name;
            const followerValue = follower && permissionModules.find(m => m.key === follower)?.permissions
                .find(p => p.name === level)?.value;
            setPermissions(prevPermissions => ({
                ...prevPermissions,
                [roleId]: {
                    ...prevPermissions[roleId],
                    [module]: value,
                    ...(followerValue ? {[follower]: followerValue} : {})
                }
            }));
        }
    }, [permissionModules]);

    useEffect(() => {
        if (permissionModules.length > 0) {
            const permissions = permissionModules.reduce((acc, data) => {
                data.permissions.forEach(permission => {
                    permission.roleIds.forEach(roleId => {
                        if (!acc[roleId]) {
                            acc[roleId] = {};
                        }
                        acc[roleId][data.key] = permission.value;
                    });
                });
                return acc;
            }, {});

            setPermissions(permissions);
            setOriginalPermissionsSnapshot(permissions);
        }
    }, [permissionModules]);

    const columns = [
        {
            accessorKey: 'description',
            header: '',
            // Maria, 19 October 2026: the section names on one line, only as wide as the longest one. They go onto two
            // lines only when the space between the role columns has all gone, rather than the table scrolling sideways.
            muiTableHeadCellProps: {sx: {width: namesWrap ? 'auto !important' : '1% !important', minWidth: '0 !important', whiteSpace: 'nowrap'}},
            muiTableBodyCellProps: {sx: {width: namesWrap ? 'auto !important' : '1% !important', minWidth: '0 !important', whiteSpace: namesWrap ? 'normal' : 'nowrap', py: 0.25, borderBottom: '1px solid #eef0f3'}},
            flex: 1,
            editable: false,
            sortable: false,
        },
        ...roles
            .filter(x => x.name === ROLES.SCHOOL_ADMIN ||
                x.name === ROLES.MEMBER_OF_STAFF ||
                x.name === ROLES.PARENT ||
                x.name === ROLES.SENIOR_MEMBER_OF_STAFF ||
                x.name === ROLES.EMPLOYEE
            )
            .filter(roleCanBeSet)
            .map(x => ({...x, order: COLUMNS_ORDER[x.name] ?? 0}))
            .sort((a, b) => a.order - b.order)
            .map(({name, id: roleId}) => ({
                accessorKey: name,
                header: ROLES_LABELS[name],
                // Maria, 19 October 2026: the five role columns share the spare room equally, so the space between them
                // is even and shrinks evenly in a narrower window
                muiTableHeadCellProps: {
                    align: 'center',
                    // a long role name goes onto two lines in a narrow window rather than widening the table
                    sx: {width: '18% !important', minWidth: '0 !important', maxWidth: 'none !important', px: 0.75, whiteSpace: 'normal', lineHeight: 1.2, '& .Mui-TableHeadCell-Content-Wrapper': {whiteSpace: 'normal'}},
                },
                muiTableBodyCellProps: {
                    align: 'center',
                    sx: {width: '18% !important', minWidth: '0 !important', maxWidth: 'none !important', px: 0.75, py: 0.25, whiteSpace: 'nowrap', borderBottom: '1px solid #eef0f3'},
                },
                editable: false,
                sortable: false,
                Header: () => <strong>{ROLES_LABELS[name]}</strong>,
                Cell: ({row}) =>
                    row.original[name] && (
                        <PermissionSelect
                            permissions={row.original[name]}
                            value={permissions[roleId]?.[row.original.module]}
                            onChange={(value) => handlePermissionChange(roleId, row.original.module, value)}
                        />
                    )
            }))
    ];

    const handleAssignPermissions = async () => {
        const changedPermissions = Object
            .entries(permissions)
            .reduce((changedPermissions, [roleId, modules]) => {
                Object
                    .values(modules)
                    .forEach((value) => {
                        if (!changedPermissions[roleId]) {
                            changedPermissions[roleId] = [];
                        }

                        changedPermissions[roleId].push(value);
                    });
                return changedPermissions;
            }, {});

        await assignPermissions({
            roleClaims: Object.entries(changedPermissions)
                .map(([roleId, claims]) => ({
                    roleId,
                    claims: claims.filter(x => x)
                }))
        });
        setOriginalPermissionsSnapshot({});
    };

    const checkIfPermissionsChanged = () => {
        for (const [roleId, modules] of Object.entries(permissions)) {
            for (const [module, value] of Object.entries(modules)) {
                const original = originalPermissionsSnapshot[roleId]?.[module];

                if (original !== undefined && value !== original) {
                    return true;
                }
            }
        }

        return false;
    };

    const assignButton = (
        <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        py: 0.25,
                        px: 1.25,
                        fontSize: 12,
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                    variant="contained"
                    startIcon={<AddTaskIcon />}
                    onClick={handleAssignPermissions}
                    disabled={isLoading || !checkIfPermissionsChanged()}
                >
                    Assign Permissions
                </Button>
    );

    return (
        <>
            {/* Maria, 19 October 2026: the three parts as tabs, with 'Assign Permissions' on the same row */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 0.5}}>
                {/* rounded buttons, as on the second row of a staff profile's Forms tab, so they do not look like the
                    School Settings tabs above */}
                <Tabs
                    value={part}
                    onChange={(e, value) => setPart(value)}
                    TabIndicatorProps={{sx: {display: 'none'}}}
                    sx={{
                        minHeight: 0,
                        '& .MuiTab-root': {
                            minHeight: 30,
                            height: 30,
                            my: 0.5,
                            mr: 1,
                            px: 2,
                            py: 0,
                            fontSize: 13,
                            textTransform: 'none',
                            fontWeight: 600,
                            color: '#0A4F45',
                            backgroundColor: '#fff',
                            border: '1px solid #9fd4c7',
                            borderRadius: '15px'
                        },
                        '& .MuiTab-root.Mui-selected': {color: '#fff', backgroundColor: '#0A8572', borderColor: '#0A8572'}
                    }}
                    data-testid="manage-access-parts"
                >
                    {PARTS.map(name => <Tab key={name} value={name} label={name} data-testid={`manage-access-${name.toLowerCase()}`}/>)}
                    {/* Maria, 20 October 2026: the words shown on hover */}
                    <Tab
                        value={RESTRICTED}
                        label={(
                            <Tooltip title="Restrict access to specific student profiles" arrow>
                                <span>{RESTRICTED}</span>
                            </Tooltip>
                        )}
                        data-testid="manage-access-restricted"
                    />
                </Tabs>
                {part === RESTRICTED
                    ? (
                        <Button
                            sx={{backgroundColor: '#186BBE', boxShadow: 'none', py: 0.25, px: 1.25, fontSize: 12, '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}}}
                            variant="contained"
                            startIcon={<AddOutlinedIcon/>}
                            onClick={() => setAddRequest(n => n + 1)}
                            data-testid="add-restriction"
                        >
                            Add Restriction
                        </Button>
                    )
                    : assignButton}
            </Stack>
            {part === RESTRICTED
                ? <StudentProfileAccessRestrictions addRequest={addRequest}/>
                : (
            <AdmicityTableV2
                tableProps={{
                    enableSorting: false,
                    enableColumnResizing: false,
                    enableBottomToolbar: false,
                    enableFilters: false,
                    enableHiding: false,
                    enableFullScreenToggle: false,
                    enableTopToolbar: false,
                    layoutMode: 'semantic',
                    muiTableProps: {sx: {tableLayout: 'auto', width: '100%'}},
                    muiTablePaperProps: {
                        sx: {
                            flexGrow: 0,
                        },
                    },
                    muiTableContainerProps: {
                        ref: containerRef,
                        sx: {
                            flex: 'none',
                            // Maria, 19 October 2026: the table at its full height, with the page scrollbar only
                            maxHeight: 'none',
                        },
                    },
                    muiTableHeadCellProps: {
                        sx: {
                            '&.MuiTableCell-root': {backgroundColor: '#e3f5f1', backgroundImage: 'none', opacity: 1},
                            fontWeight: 600,
                            py: 0.75,
                        },
                    },
                    muiTableBodyCellProps: {
                        sx: {
                            borderBottom: '1px solid #eef0f3',
                            paddingTop: 0.25,
                            paddingBottom: 0.25
                        },
                    }, // Maria, 20 October 2026: every other row shaded light green, so it is easy to follow a row across
                    muiTableBodyRowProps: ({row}) => ({
                        // Maria, 20 October 2026: a little more room in each row, the same height as the Manage Users tables
                        sx: {height: 38, '& td.MuiTableCell-root': {backgroundColor: row.index % 2 === 1 ? '#f1faf7' : '#fff'}}
                    }),
                }}
                data={permissionModules && roles.length > 0
                    // Maria, 19 October 2026: Admissions Categories is not listed, as the categories are public
                    // information; its permissions are kept as they are and saved with the rest
                    ? visibleModules.map((module, index) => ({
                        id: index,
                        description: SHORT_NAMES[module.description] ?? module.description,
                        module: module.key,
                        ...roles
                            .reduce((roleItems, role) => ({
                                ...roleItems,
                                [role.name]: module.permissions.map(permission => ({
                                    name: permission.name,
                                    value: permission.value,
                                    disabled: permission.disabledForRoleIds.includes(role.id)
                                }))
                            }), {})
                    }))
                    : []}
                columns={columns}
                isLoading={isLoading}
                hideFooterPagination
            />
                )}
        </>
    );
};

export default AccessSettings;